import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/seo/blog/posts?status=published`);
      setPosts(response.data.posts);
    } catch (err) {
      setError('Failed to load blog posts');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-16">
        <div className="container mx-auto px-4 text-center">
          <p className="text-gray-600">Loading blog posts...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>Dental News | Fort Worth Dental | Tips & Insights</title>
        <meta name="description" content="Expert dental health tips, implant insights, and oral care advice from Fort Worth Dental. Learn about dental implants, Invisalign, and more from Drs. Mund, Razo, Hiwiadi, and Pham." />
        <meta name="keywords" content="dental news, dental health tips, fort worth dentist news, dental implant information, invisalign tips, oral health advice" />
        <link rel="canonical" href="https://fortworthdental.com/blogs" />
        <meta property="og:url" content="https://fortworthdental.com/blogs" />
        <meta property="og:title" content="Dental News | Fort Worth Dental" />
        <meta property="og:description" content="Expert dental health tips and insights from Fort Worth Dental." />
      </Helmet>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-teal-600 to-teal-800 text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">Fort Worth Dental News</h1>
          <p className="text-xl">Expert Dental Tips &amp; Insights from Dr. Mund, Dr. Razo, Dr. Hiwiadi &amp; Dr. Pham</p>
        </div>
      </section>

      <div className="container mx-auto px-4 py-12">
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
            {error}
          </div>
        )}

        {posts.length === 0 ? (
          <div className="text-center py-12">
            <h2 className="text-2xl text-gray-600 mb-4">No blog posts yet</h2>
            <p className="text-gray-500">Check back soon for dental tips and insights!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {posts.map((post) => (
              <article 
                key={post.id} 
                className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow"
              >
                <div className="bg-teal-600 h-2"></div>
                <div className="p-6">
                  <div className="flex items-center text-sm text-gray-500 mb-3">
                    <span className="bg-teal-100 text-teal-800 px-2 py-1 rounded text-xs font-semibold">
                      {post.topic}
                    </span>
                    <span className="mx-2">•</span>
                    <span>{new Date(post.published_at).toLocaleDateString()}</span>
                  </div>
                  
                  <h2 className="text-xl font-bold text-gray-800 mb-3 hover:text-teal-600">
                    <Link to={`/blogs/${post.slug}`}>
                      {post.title}
                    </Link>
                  </h2>
                  
                  <p className="text-gray-600 mb-4 line-clamp-3">
                    {post.excerpt}
                  </p>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">
                      By <span className="font-semibold text-teal-600">{post.author}</span>
                    </span>
                    <Link 
                      to={`/blogs/${post.slug}`}
                      className="text-teal-600 font-semibold hover:underline"
                    >
                      Read More →
                    </Link>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}

        {/* SEO Footer Section */}
        <section className="mt-16 bg-white p-8 rounded-lg shadow-md">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            About Our Dental News
          </h2>
          <p className="text-gray-600 mb-4">
            Welcome to Fort Worth Dental News, where Dr. Mund, Dr. Razo, Dr. Hiwiadi, and
            Dr. Pham share expert insights on dental health, dental implants, Invisalign,
            cosmetic dentistry, and more. Our team is dedicated to helping patients in Fort
            Worth, Aledo, Benbrook, and surrounding areas maintain healthy, beautiful smiles.
          </p>
          <div className="flex flex-wrap gap-2">
            <span className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm">Dental Implants</span>
            <span className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm">Invisalign</span>
            <span className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm">Cosmetic Dentistry</span>
            <span className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm">Dental Health Tips</span>
            <span className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm">Fort Worth Dentist</span>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Blog;
